import type { IDurationObject } from "app/services/DurationService";
import { countryByIdFilter, minAbbrNumberFilter, percentageFilter } from "app/filters/ngFilters";
import type { KeywordList } from "app/modules/demand-analysis/keyword-lists";
import type { ICountryObject } from "app/services/CountryService";

import { PERIOD_OVER_PERIOD_COLOR_SCALE } from "./constants";
import type { TrafficByCountryVariant } from "./types";

export const getTrafficByCountryVariant = (
    keywordListsSize: number,
    isPeriodOverPeriod: boolean,
): TrafficByCountryVariant => {
    if (keywordListsSize === 0) {
        return "SinglePeriod";
    } else if (keywordListsSize === 1) {
        return isPeriodOverPeriod ? "PeriodOverPeriod" : "SinglePeriod";
    } else {
        return isPeriodOverPeriod ? "ComparisonAndPoP" : "Comparison";
    }
};

const excelRoutesMap: Record<TrafficByCountryVariant, string> = {
    SinglePeriod: "/api/KeywordGroupsByCountriesAnalysis/Excel",
    Comparison: "/api/KeywordGroupsByCountriesAnalysis/Compare/Excel",
    PeriodOverPeriod: "/api/KeywordGroupsByCountriesAnalysis/PoP/Excel",
    ComparisonAndPoP: "/api/KeywordGroupsByCountriesAnalysis/ComparePoP/Excel",
};

const getExcelUrl = (
    variant: TrafficByCountryVariant,
    durationData: IDurationObject<string>,
    keywordLists: KeywordList[],
) => {
    const apiExcelPath = excelRoutesMap[variant];
    const urlParams = new URLSearchParams({ from: durationData.from, to: durationData.to });

    if (keywordLists.length === 1) {
        urlParams.set("keywordListId", keywordLists[0].keywordListId);
    }

    if (keywordLists.length > 1) {
        urlParams.set("keywordListIds", keywordLists.map((keywordList) => keywordList.keywordListId).join(","));
    }

    if (durationData.compareFrom && durationData.compareTo) {
        urlParams.set("compareFrom", durationData.compareFrom);
        urlParams.set("compareTo", durationData.compareTo);
    }

    return `${apiExcelPath}?${urlParams.toString()}`;
};

export const getPeriodOverPeriodCountryColor = (value: number, minValue: number, maxValue: number) => {
    if (value < 0) {
        const ratio = (value - minValue) / (0 - minValue);
        const colorIndex = Math.round(ratio * (PERIOD_OVER_PERIOD_COLOR_SCALE.red.length - 1));
        return PERIOD_OVER_PERIOD_COLOR_SCALE.red[PERIOD_OVER_PERIOD_COLOR_SCALE.red.length - 1 - colorIndex];
    } else {
        const ratio = (value - 0) / (maxValue - 0);
        const colorIndex = Math.round(ratio * (PERIOD_OVER_PERIOD_COLOR_SCALE.green.length - 1));
        return PERIOD_OVER_PERIOD_COLOR_SCALE.green[colorIndex];
    }
};

export const countryById: (countryId: number) => ICountryObject = countryByIdFilter();
export const volumeFormatter = minAbbrNumberFilter();
export const changeValueFormatter = (value: number) => `${percentageFilter()(value, 2)}%`;
export const shareValueFormatter = (value: number) => `${percentageFilter()(value, 2)}%`;

export const WORLDWIDE_SUBTITLE_FILTER = {
    filter: "country",
    value: "Worldwide",
    countryCode: 999,
};
